import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BillingGrpcService } from './billing-grpc.service';
import { FirebaseAuthService } from './firebase-auth.service';

/**
 * Load state of the user's balance, so the UI can tell "still loading" apart from
 * "we tried and failed" (Issue #1653).
 *
 * - `loading` — a fetch is in flight (or has not been attempted yet because auth is not ready).
 * - `loaded`  — `balance$` holds a real, server-confirmed value.
 * - `error`   — every bounded retry failed; `balance$` is deliberately left `null` so no
 *               stale or fabricated `$0.00` amount is ever presented as the current balance.
 */
export type BalanceLoadStatus = 'loading' | 'loaded' | 'error';

export interface BalanceCheckResult {
  hasSufficientBalance: boolean;
  billingUnavailable: boolean;
  currentBalance: { currencyCode: string; units: number; nanos: number };
  shortfall?: { currencyCode: string; units: number; nanos: number };
  heldBalance?: { currencyCode: string; units: number; nanos: number };
}

export interface UserBillingProfile {
  userEmail: string;
  availableBalance: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  heldBalance: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  ledgerBalance: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  subscriptionTier: string | number;
  complimentaryCredits: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  createdAt: string;
  updatedAt: string;
  stripeCustomerId: string;
  usageStats: {
    totalSpent: {
      currencyCode: string;
      units: number;
      nanos: number;
    };
    totalTasksExecuted: number;
    lastTaskDate?: string;
    monthlySpend: {
      currencyCode: string;
      units: number;
      nanos: number;
    };
  };
}

export interface BillingTransaction {
  transactionId: string;
  userEmail: string;
  type: string;
  amount: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  balanceBefore: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  balanceAfter: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  description: string;
  createdAt: string;
  taskId?: string;
  stripePaymentIntentId?: string;
  metadata?: any;
}

export interface TaskTypeExpenseSummary {
  taskType: string;
  taskCount: number;
  totalCost: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  fileCount: number;
  pageCount: number;
  avgCostPerTask: number;
  costStdDev: number;
  avgDurationSeconds: number;
  durationStdDev: number;
  minCostPerTask: number;
  maxCostPerTask: number;
  minDurationSeconds: number;
  maxDurationSeconds: number;
  lastActivityAt?: string;
  collaboratorContributions?: CollaboratorContribution[];
}

export interface CollaboratorContribution {
  userEmail: string;
  displayName: string;
  photoUrl?: string;
  taskCount: number;
  totalCost: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  fileCount: number;
  pageCount: number;
  avgCostPerTask: number;
  costStdDev: number;
  avgDurationSeconds: number;
  durationStdDev: number;
  lastActivityAt?: string;
}

/**
 * One page of transaction history (Issue #1533).
 *
 * `nextPageToken` is an opaque cursor for the following page; an empty string means the last page
 * has been reached.
 */
export interface TransactionHistoryPage {
  transactions: BillingTransaction[];
  nextPageToken: string;
}

export interface ProjectExpenseSummary {
  projectId: string;
  projectName?: string;
  totalCost: {
    currencyCode: string;
    units: number;
    nanos: number;
  };
  taskCount: number;
  recentTransactions: BillingTransaction[];
  taskTypeBreakdown: TaskTypeExpenseSummary[];
  fileCount: number;
  pageCount: number;
  avgDurationSeconds: number;
  durationStdDev: number;
  minCostPerTask: number;
  maxCostPerTask: number;
  minDurationSeconds: number;
  maxDurationSeconds: number;
}

export interface TopUpResponse {
  success: boolean;
  message: string;
  stripePaymentIntentId: string;
  /** Stripe-hosted Checkout Session URL to redirect the browser to. */
  checkoutUrl: string;
  transaction: BillingTransaction;
}

/**
 * Service for managing user billing operations.
 *
 * This service provides methods for:
 * - Getting user billing profile and balance
 * - Processing balance top-ups via Stripe
 * - Retrieving transaction and expense history
 * - Real-time balance updates
 */
@Injectable({
  providedIn: 'root',
})
export class BillingService {
  private billingGrpcService = inject(BillingGrpcService);
  private authService = inject(FirebaseAuthService);

  /** Total attempts (initial + retries) for one balance load cycle. */
  private static readonly MAX_LOAD_ATTEMPTS = 3;
  /** Delay before the first retry; doubled after each further failure. */
  private static readonly INITIAL_RETRY_DELAY_MS = 1_000;
  private static readonly RETRY_BACKOFF_MULTIPLIER = 2;
  /** Jitter range: delay is multiplied by a random factor in [1 - JITTER, 1 + JITTER]. */
  private static readonly RETRY_JITTER = 0.25;

  private balanceSubject = new BehaviorSubject<number | null>(null);
  private heldBalanceSubject = new BehaviorSubject<number | null>(null);
  private profileSubject = new BehaviorSubject<UserBillingProfile | null>(null);
  private balanceStatusSubject = new BehaviorSubject<BalanceLoadStatus>('loading');

  balance$ = this.balanceSubject.asObservable();
  heldBalance$ = this.heldBalanceSubject.asObservable();
  profile$ = this.profileSubject.asObservable();
  /** Emits whether the balance is loading, loaded, or failed to load (Issue #1653). */
  balanceStatus$ = this.balanceStatusSubject.asObservable().pipe(distinctUntilChanged());

  /** De-duplicates concurrent user-initiated retries so a rapid click cannot storm the backend. */
  private pendingRetry: Promise<void> | null = null;
  /** uid of the signed-in user the current balance belongs to (null when signed out). */
  private loadedForUserId: string | null = null;

  constructor() {
    ClientLoggingService.log('INFO', '[BillingService] Constructor called, initializing service...');
    this.watchAuthState();
  }

  /**
   * Loads the balance once the user is actually signed in, and reloads it when the signed-in
   * identity changes.
   *
   * Before Issue #1653 the constructor fired a single unguarded fetch during app bootstrap. If
   * that raced auth readiness (or hit a cold-start/network blip) the failure was only logged and
   * `balance$` stayed `null` forever, which the header chip rendered as an endless shimmer.
   */
  private watchAuthState(): void {
    this.authService.user$
      .pipe(distinctUntilChanged((previous, next) => (previous?.uid ?? null) === (next?.uid ?? null)))
      .subscribe(user => {
        const previousUserId = this.loadedForUserId;

        // `distinctUntilChanged` above means reaching here always signifies an identity change:
        // a sign-in, a sign-out, or a direct account switch. Whatever is cached belongs to the
        // previous identity, so it is dropped in every one of those cases — a switch straight
        // from one signed-in user to another would otherwise keep showing the old balance until
        // the new user's load returns.
        this.resetBillingStateForIdentityChange(user?.uid ?? null);

        if (user) {
          ClientLoggingService.log(
            'INFO',
            previousUserId === null
              ? '[BillingService] Auth ready, loading billing profile for signed-in user'
              : '[BillingService] Signed-in user changed, reloading billing profile for the new account'
          );
          void this.loadBalanceWithRetry();
          return;
        }

        if (previousUserId !== null) {
          ClientLoggingService.log('INFO', '[BillingService] User signed out, clearing cached billing state');
        }
      });
  }

  /**
   * Drops every piece of cached billing state so nothing from the previous identity can be
   * observed by the next one.
   */
  private resetBillingStateForIdentityChange(nextUserId: string | null): void {
    // Updated first: in-flight loads compare against this to decide whether their result is
    // still wanted, so it must change before anything else.
    this.loadedForUserId = nextUserId;
    // Drop the de-duplication slot too. Otherwise a retry still running for the previous session
    // would swallow the next user's first retry click as a "duplicate".
    this.pendingRetry = null;
    this.profileSubject.next(null);
    this.balanceSubject.next(null);
    this.heldBalanceSubject.next(null);
    this.balanceStatusSubject.next('loading');
  }

  /**
   * True when the signed-in identity changed since an async load began, meaning that load's
   * result must be discarded rather than published.
   *
   * Without this, an in-flight load can outlive a sign-out or account switch and republish the
   * previous session's balance — or a terminal `error` — onto the next identity.
   */
  private isStaleFor(expectedUserId: string | null): boolean {
    if (this.loadedForUserId === expectedUserId) {
      return false;
    }
    ClientLoggingService.log(
      'INFO',
      '[BillingService] Signed-in identity changed mid-flight; discarding this billing load instead of ' +
        'publishing it to the new session'
    );
    return true;
  }

  /**
   * Loads the billing profile, retrying transient failures with exponential backoff + jitter.
   *
   * Bounded on purpose (`MAX_LOAD_ATTEMPTS`): a transient failure self-heals without any user
   * action, while a persistent one settles into a terminal `error` status the UI can render as a
   * distinct, actionable state instead of shimmering forever.
   */
  private async loadBalanceWithRetry(): Promise<void> {
    // Whose balance this cycle is for. Every resumption point below re-checks it, so a loop
    // orphaned by a sign-out or account switch abandons quietly instead of publishing.
    const expectedUserId = this.loadedForUserId;
    this.balanceStatusSubject.next('loading');
    let delayMs = BillingService.INITIAL_RETRY_DELAY_MS;

    for (let attempt = 1; attempt <= BillingService.MAX_LOAD_ATTEMPTS; attempt++) {
      // The loop owns the terminal status: a single failed attempt must not flip the chip to
      // `error` while further attempts are still coming.
      const profile = await this.loadUserBillingProfile({
        publishTerminalStatus: false,
        expectedUserId,
      });
      if (this.isStaleFor(expectedUserId)) {
        return;
      }
      if (profile) {
        this.balanceStatusSubject.next('loaded');
        return;
      }

      if (attempt < BillingService.MAX_LOAD_ATTEMPTS) {
        const wait = BillingService.jitteredDelay(delayMs);
        ClientLoggingService.log(
          'WARN',
          `[BillingService] Balance load attempt ${attempt}/${BillingService.MAX_LOAD_ATTEMPTS} failed, ` +
            `retrying in ${wait}ms`
        );
        await BillingService.sleep(wait);
        if (this.isStaleFor(expectedUserId)) {
          return;
        }
        delayMs = Math.round(delayMs * BillingService.RETRY_BACKOFF_MULTIPLIER);
      }
    }

    ClientLoggingService.log(
      'ERROR',
      `[BillingService] Balance load failed after ${BillingService.MAX_LOAD_ATTEMPTS} attempts; ` +
        'surfacing an error state so the header chip can offer a retry'
    );
    this.balanceStatusSubject.next('error');
  }

  /**
   * User-initiated retry, wired to the header balance chip's error state.
   *
   * Concurrent invocations share one in-flight attempt so repeated clicks cannot produce a
   * request storm.
   */
  retryBalanceLoad(): Promise<void> {
    if (this.pendingRetry) {
      ClientLoggingService.log('INFO', '[BillingService] Balance retry already in flight, ignoring duplicate request');
      return this.pendingRetry;
    }

    ClientLoggingService.log('INFO', '[BillingService] User-initiated balance retry requested');
    // Only clear the slot if this attempt still owns it. A sign-out clears `pendingRetry`
    // eagerly, and without this check a late-settling previous attempt would null out the new
    // session's in-flight retry.
    const tracked: Promise<void> = this.loadBalanceWithRetry().finally(() => {
      if (this.pendingRetry === tracked) {
        this.pendingRetry = null;
      }
    });
    this.pendingRetry = tracked;
    return tracked;
  }

  private static jitteredDelay(base: number): number {
    const factor = 1 - BillingService.RETRY_JITTER + Math.random() * 2 * BillingService.RETRY_JITTER;
    return Math.round(base * factor);
  }

  private static sleep(ms: number): Promise<void> {
    return new Promise<void>(resolve => setTimeout(resolve, ms));
  }

  /**
   * Loads the user's billing profile and updates observables.
   *
   * Performs exactly one attempt. On failure it returns `null` and — unless a retry loop is
   * already driving further attempts — moves `balanceStatus$` to `error` so the UI can show a
   * distinct, actionable failure state rather than an indefinite loading shimmer.
   *
   * The response is published only if the signed-in identity is unchanged since the request
   * began. That guard lives here rather than only in the retry loop because this method is also
   * reachable from `releaseTaskCostHold()` and the Billing page, neither of which goes through
   * the loop.
   *
   * @param options.publishTerminalStatus set false when a retry loop owns the terminal status.
   * @param options.expectedUserId identity to validate the response against; defaults to whoever
   *     is signed in when the call starts.
   */
  async loadUserBillingProfile(
    options: { publishTerminalStatus?: boolean; expectedUserId?: string | null } = {}
  ): Promise<UserBillingProfile | null> {
    const publishTerminalStatus = options.publishTerminalStatus ?? true;
    const expectedUserId = options.expectedUserId !== undefined ? options.expectedUserId : this.loadedForUserId;
    ClientLoggingService.log('INFO', '[BillingService] loadUserBillingProfile() starting...');
    this.balanceStatusSubject.next('loading');

    try {
      ClientLoggingService.log('INFO', '[BillingService] Calling billingGrpcService.getUserBillingProfile()...');
      const observable = this.billingGrpcService.getUserBillingProfile({});
      ClientLoggingService.log('INFO', '[BillingService] Observable created:', !!observable);

      ClientLoggingService.log('INFO', '[BillingService] Converting observable to promise with firstValueFrom...');
      const response = await firstValueFrom(observable);
      ClientLoggingService.log('INFO', '[BillingService] Response received:', response);

      // Extract profile from response (response is GetUserBillingProfileResponse)
      ClientLoggingService.log('INFO', '[BillingService] Getting profile from response...');
      const profile = response.getProfile();
      ClientLoggingService.log('INFO', '[BillingService] Profile from getProfile():', profile);

      if (!profile) {
        throw new Error('No profile returned from server');
      }

      // Convert to object for easier handling
      const profileObj = profile.toObject();
      ClientLoggingService.log('INFO', '[BillingService] Profile object:', profileObj);

      const zeroMoney = { currencyCode: 'USD', units: 0, nanos: 0 };
      const timestampToIso = (timestamp?: { seconds: number }): string =>
        timestamp ? new Date(timestamp.seconds * 1000).toISOString() : '';
      const normalizedProfile: UserBillingProfile = {
        userEmail: profileObj.userEmail,
        availableBalance: profileObj.availableBalance ?? zeroMoney,
        heldBalance: profileObj.heldBalance ?? zeroMoney,
        ledgerBalance: profileObj.ledgerBalance ?? profileObj.availableBalance ?? zeroMoney,
        subscriptionTier: profileObj.subscriptionTier,
        complimentaryCredits: profileObj.complimentaryCredits ?? zeroMoney,
        createdAt: timestampToIso(profileObj.createdAt),
        updatedAt: timestampToIso(profileObj.updatedAt),
        stripeCustomerId: profileObj.stripeCustomerId,
        usageStats: {
          totalSpent: profileObj.usageStats?.totalSpent ?? zeroMoney,
          totalTasksExecuted: profileObj.usageStats?.totalTasksExecuted ?? 0,
          lastTaskDate: profileObj.usageStats?.lastTaskDate
            ? timestampToIso(profileObj.usageStats.lastTaskDate)
            : undefined,
          monthlySpend: profileObj.usageStats?.monthlySpend ?? zeroMoney,
        },
      };
      // The identity may have changed while this request was in flight. Publishing now would
      // briefly show the previous user's balance to the new one.
      if (this.isStaleFor(expectedUserId)) {
        return null;
      }

      this.profileSubject.next(normalizedProfile);
      this.balanceSubject.next(this.moneyToNumber(normalizedProfile.availableBalance));
      this.heldBalanceSubject.next(this.moneyToNumber(normalizedProfile.heldBalance));

      this.balanceStatusSubject.next('loaded');

      ClientLoggingService.log('INFO', '[BillingService] Profile and balance updated successfully');
      return normalizedProfile;
    } catch (error: any) {
      const grpcCode = error?.code;
      const grpcMessage = error?.message;
      ClientLoggingService.log(
        'ERROR',
        `[BillingService] Failed to load user billing profile: code=${grpcCode}, message=${grpcMessage}`
      );
      // Deliberately leave balance$/profile$ untouched-but-unpublished: never fabricate $0.00 and
      // never keep presenting a stale amount as if it were current (Issue #1653).
      // A failure that belongs to a session the user has already left must not surface either.
      if (publishTerminalStatus && !this.isStaleFor(expectedUserId)) {
        this.balanceStatusSubject.next('error');
      }
      return null;
    }
  }

  /**
   * Initiates a balance top-up via Stripe-hosted Checkout.
   *
   * <p>Returns a {@link TopUpResponse} whose {@code checkoutUrl} the caller should redirect the
   * browser to. The balance is credited asynchronously by the Stripe webhook once payment
   * completes, so callers should refresh the profile after the user returns from Checkout.
   */
  async topUpBalance(amount: number): Promise<TopUpResponse> {
    const request = {
      amount: {
        currencyCode: 'USD',
        units: Math.floor(amount),
        nanos: Math.round((amount - Math.floor(amount)) * 1_000_000_000),
      },
      returnUrl: window.location.origin + '/billing',
    };

    ClientLoggingService.log('INFO', '[BillingService] Calling topUpBalance with request:', request);

    const grpcResponse = await firstValueFrom(this.billingGrpcService.topUpBalance(request));

    ClientLoggingService.log('INFO', '[BillingService] Received gRPC response:', grpcResponse);

    // Convert protobuf response to plain object
    const responseObj = grpcResponse.toObject();
    ClientLoggingService.log('INFO', '[BillingService] Response object:', responseObj);

    // Convert to TopUpResponse interface
    const response: TopUpResponse = {
      success: responseObj.success,
      message: responseObj.message,
      stripePaymentIntentId: responseObj.stripePaymentIntentId,
      checkoutUrl: responseObj.checkoutUrl,
      transaction: responseObj.transaction as any,
    };

    ClientLoggingService.log('INFO', '[BillingService] Converted response:', response);

    return response;
  }

  /**
   * Gets one page of the user's transaction history, newest first.
   *
   * Paging is cursor-based (Issue #1533): pass the `nextPageToken` from the previous page to fetch
   * the following one. An empty `nextPageToken` in the result means there are no older rows.
   *
   * Unlike the other read helpers here, failures are re-thrown rather than swallowed into an empty
   * list, so the caller can tell "no transactions" apart from "the request failed" and surface a
   * retry affordance for the page the user asked for.
   */
  async getTransactionHistory(
    limit?: number,
    typeFilter?: string,
    pageToken?: string
  ): Promise<TransactionHistoryPage> {
    try {
      const request: any = {};
      if (limit) request.limit = limit;
      if (typeFilter) request.typeFilter = typeFilter;
      if (pageToken) request.pageToken = pageToken;

      const response = await firstValueFrom(this.billingGrpcService.getTransactionHistory(request));
      const transactions = response.getTransactionsList();
      return {
        transactions: transactions.map(t => t.toObject()) as any[],
        nextPageToken: response.getNextPageToken() || '',
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Failed to get transaction history:', error);
      throw error;
    }
  }

  /**
   * Gets the user's expense history grouped by projects.
   *
   * Failures are re-thrown so callers can distinguish an empty ledger from an unavailable
   * accounting service and present the appropriate retry state.
   */
  async getExpenseHistory(
    limit?: number,
    includeCollaborators = false
  ): Promise<{
    projectExpenses: ProjectExpenseSummary[];
    totalExpenses: { currencyCode: string; units: number; nanos: number };
  }> {
    try {
      const request: any = {};
      if (limit) request.limit = limit;
      if (includeCollaborators) request.includeCollaborators = true;

      const response = await firstValueFrom(this.billingGrpcService.getExpenseHistory(request));
      const responseObj = response.toObject();

      // Convert protobuf AsObject to our interface format
      // Cast to any: proto TS types may lag behind billing.proto until next codegen
      const mapCollaboratorContributions = (list: any[]): CollaboratorContribution[] =>
        (list || []).map((c: any) => ({
          userEmail: c.userEmail,
          displayName: c.displayName,
          photoUrl: c.photoUrl || undefined,
          taskCount: c.taskCount,
          totalCost: c.totalCost || { currencyCode: 'USD', units: 0, nanos: 0 },
          fileCount: c.fileCount || 0,
          pageCount: c.pageCount || 0,
          avgCostPerTask: c.avgCostPerTask || 0,
          costStdDev: c.costStdDev || 0,
          avgDurationSeconds: c.avgDurationSeconds || 0,
          durationStdDev: c.durationStdDev || 0,
          lastActivityAt: c.lastActivityAt?.seconds
            ? new Date(c.lastActivityAt.seconds * 1000).toISOString()
            : undefined,
        }));

      const projectExpenses: ProjectExpenseSummary[] = responseObj.projectExpensesList.map((expense: any) => ({
        projectId: expense.projectId,
        projectName: expense.projectName,
        totalCost: expense.totalCost || { currencyCode: 'USD', units: 0, nanos: 0 },
        taskCount: expense.taskCount,
        recentTransactions: (expense.recentTransactionsList || []) as any[],
        taskTypeBreakdown: (expense.taskTypeBreakdownList || []).map((b: any) => ({
          taskType: b.taskType,
          taskCount: b.taskCount,
          totalCost: b.totalCost || { currencyCode: 'USD', units: 0, nanos: 0 },
          fileCount: b.fileCount || 0,
          pageCount: b.pageCount || 0,
          avgCostPerTask: b.avgCostPerTask || 0,
          costStdDev: b.costStdDev || 0,
          avgDurationSeconds: b.avgDurationSeconds || 0,
          durationStdDev: b.durationStdDev || 0,
          minCostPerTask: b.minCostPerTask || 0,
          maxCostPerTask: b.maxCostPerTask || 0,
          minDurationSeconds: b.minDurationSeconds || 0,
          maxDurationSeconds: b.maxDurationSeconds || 0,
          lastActivityAt: b.lastActivityAt?.seconds
            ? new Date(b.lastActivityAt.seconds * 1000).toISOString()
            : undefined,
          collaboratorContributions: mapCollaboratorContributions(b.collaboratorContributionsList),
        })),
        fileCount: expense.fileCount || 0,
        pageCount: expense.pageCount || 0,
        avgDurationSeconds: expense.avgDurationSeconds || 0,
        durationStdDev: expense.durationStdDev || 0,
        minCostPerTask: expense.minCostPerTask || 0,
        maxCostPerTask: expense.maxCostPerTask || 0,
        minDurationSeconds: expense.minDurationSeconds || 0,
        maxDurationSeconds: expense.maxDurationSeconds || 0,
      }));

      return {
        projectExpenses,
        totalExpenses: responseObj.totalExpenses || { currencyCode: 'USD', units: 0, nanos: 0 },
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Failed to get expense history:', error);
      throw error;
    }
  }

  /**
   * Gets project expense history (project owner only).
   */
  async getProjectExpenseHistory(
    projectId: string,
    limit?: number
  ): Promise<{
    expenses: any[];
    totalProjectCost: { currencyCode: string; units: number; nanos: number };
  }> {
    try {
      const request: any = { projectId };
      if (limit) request.limit = limit;

      const response = await firstValueFrom(this.billingGrpcService.getProjectExpenseHistory(request));
      return {
        expenses: response.expenses,
        totalProjectCost: response.totalProjectCost,
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Failed to get project expense history:', error);
      return {
        expenses: [],
        totalProjectCost: { currencyCode: 'USD', units: 0, nanos: 0 },
      };
    }
  }

  /**
   * Checks if user has sufficient balance for an estimated cost.
   */
  async checkSufficientBalance(
    estimatedCost: number,
    taskType?: string,
    taskCount: number = 1,
    operationId?: string
  ): Promise<BalanceCheckResult> {
    try {
      const request = {
        userEmail: '', // Will be filled by backend from auth context
        estimatedCost: {
          currencyCode: 'USD',
          units: Math.floor(estimatedCost),
          nanos: Math.round((estimatedCost - Math.floor(estimatedCost)) * 1_000_000_000),
        },
        taskType,
        taskCount,
        operationId,
      };

      const response = await firstValueFrom(this.billingGrpcService.checkSufficientBalance(request));
      const responseObj = response.toObject();
      return {
        hasSufficientBalance: responseObj.hasSufficientBalance,
        billingUnavailable: false,
        currentBalance: responseObj.currentBalance ?? { currencyCode: 'USD', units: 0, nanos: 0 },
        shortfall: responseObj.shortfall,
        heldBalance: responseObj.heldBalance,
      };
    } catch (error) {
      ClientLoggingService.log(
        'ERROR',
        `[BillingService] Billing preflight unavailable for operation ${operationId || 'untracked'}; no task RPCs will be dispatched`,
        error
      );
      return {
        hasSufficientBalance: false,
        billingUnavailable: true,
        currentBalance: { currencyCode: 'USD', units: 0, nanos: 0 },
      };
    }
  }

  /**
   * Proactive balance guard for task-creation flows. Returns true whenever dispatch must stop:
   * either funds are insufficient or wallet readiness could not be established.
   */
  async guardInsufficientBalance(
    snackBar: MatSnackBar,
    router: Router,
    taskType?: string,
    taskCount: number = 1,
    operationId?: string
  ): Promise<boolean> {
    const result = await this.checkSufficientBalance(0, taskType, taskCount, operationId);
    if (result.billingUnavailable) {
      snackBar.open(
        'Billing validation is unavailable. No ingestion or paid work was started. Please try again; if this continues, contact support.',
        'Dismiss',
        {
          duration: 10000,
          panelClass: ['error-snackbar'],
        }
      );
      return true;
    }
    if (!result.hasSufficientBalance) {
      const message = result.shortfall
        ? `Insufficient available balance. ${this.formatMoney(result.shortfall)} needed including in-flight work.`
        : 'Insufficient available balance. Additional funds are needed including in-flight work.';
      snackBar
        .open(message, 'Go to Billing', {
          duration: 10000,
          panelClass: ['error-snackbar'],
        })
        .onAction()
        .subscribe(() => router.navigate(['/billing']));
      return true;
    }
    return false;
  }

  async releaseTaskCostHold(taskId: string, reason: string): Promise<void> {
    await firstValueFrom(this.billingGrpcService.releaseTaskCostHold(taskId, reason));
    await this.refreshBalance();
  }

  /**
   * Refreshes the user's balance from the server, retrying transient failures with backoff so a
   * post-task refresh blip does not strand the header chip (Issue #1653).
   */
  async refreshBalance(): Promise<void> {
    await this.loadBalanceWithRetry();
  }

  /**
   * Formats a Money object as a currency string.
   */
  formatMoney(money: { currencyCode: string; units: number; nanos: number }): string {
    const amount = this.moneyToNumber(money);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: money.currencyCode,
    }).format(amount);
  }

  /**
   * Converts a Money object to a number.
   */
  moneyToNumber(money: { currencyCode: string; units: number; nanos: number }): number {
    return money.units + money.nanos / 1_000_000_000;
  }

  /**
   * Converts a number to a Money object.
   */
  numberToMoney(amount: number, currencyCode: string = 'USD'): { currencyCode: string; units: number; nanos: number } {
    return {
      currencyCode,
      units: Math.floor(amount),
      nanos: Math.round((amount - Math.floor(amount)) * 1_000_000_000),
    };
  }

  /**
   * Gets a human-readable transaction type label.
   */
  getTransactionTypeLabel(type: string | number): string {
    // Handle both string and enum number values
    const typeStr = typeof type === 'number' ? this.transactionTypeEnumToString(type) : type;
    switch (typeStr?.toLowerCase()) {
      case 'credit':
        return 'Balance Top-up';
      case 'debit':
        return 'Task Cost';
      case 'refund':
        return 'Refund';
      case 'adjustment':
        return 'Adjustment';
      case 'complimentary':
        return 'Complimentary Credits';
      case 'non_charged':
        return 'Task Failed (No Charge)';
      case 'authorization_hold':
        return 'Hold Placed';
      case 'authorization_release':
        return 'Hold Released';
      case 'authorization_settlement':
        return 'Hold Settled';
      default:
        return 'Unknown';
    }
  }

  /**
   * Gets a CSS class for transaction type styling.
   */
  getTransactionTypeClass(type: string | number): string {
    // Handle both string and enum number values
    const typeStr = typeof type === 'number' ? this.transactionTypeEnumToString(type) : type;
    switch (typeStr?.toLowerCase()) {
      case 'credit':
        return 'transaction-credit';
      case 'debit':
        return 'transaction-debit';
      case 'refund':
        return 'transaction-refund';
      case 'adjustment':
        return 'transaction-adjustment';
      case 'complimentary':
        return 'transaction-complimentary';
      case 'non_charged':
        return 'transaction-non-charged';
      case 'authorization_hold':
        return 'transaction-authorization-hold';
      case 'authorization_release':
        return 'transaction-authorization-release';
      case 'authorization_settlement':
        return 'transaction-authorization-settlement';
      default:
        return 'transaction-unknown';
    }
  }

  /**
   * Converts transaction type enum number to string.
   */
  private transactionTypeEnumToString(type: number): string {
    // Based on TransactionType enum in billing.proto
    switch (type) {
      case 0:
        return 'unspecified';
      case 1:
        return 'credit';
      case 2:
        return 'debit';
      case 3:
        return 'refund';
      case 4:
        return 'adjustment';
      case 5:
        return 'complimentary';
      case 6:
        return 'non_charged';
      case 7:
        return 'authorization_hold';
      case 8:
        return 'authorization_release';
      case 9:
        return 'authorization_settlement';
      default:
        return 'unknown';
    }
  }
}
